import { useContext, useEffect, useMemo } from 'react';
import { PaginationContext, Slideshow } from 'dash-ui';
import { useTranslation } from 'react-i18next';

import {
  GetPropertyByIdQuery,
  useGetMediaFoldersAndFilesQuery,
  useObserveMediaChangesSubscription,
} from 'gql/generated.graphql';
import { downloadMedia } from 'common/helpers/downloadMedia';

const PropertySlideshow = ({
  property,
}: {
  property: GetPropertyByIdQuery['getPropertyById'];
}) => {
  const { t } = useTranslation(['common, pages']);
  const { getRefetchProps, setTotal, state, setPerPage } =
    useContext(PaginationContext);
  const { skip, limit } = getRefetchProps(state);
  const filter = {
    propertyId: property._id,
    mediaFolderId: property.slideshowMediaFolderId,
  };
  const { data, refetch, loading } = useGetMediaFoldersAndFilesQuery({
    variables: { filter, pagination: { limit, skip } },
  });

  useObserveMediaChangesSubscription({
    variables: { filter },
    onData() {
      refetch();
    },
  });

  const { mediaFiles, totalCount } = useMemo(
    () => ({
      mediaFiles: data?.getMediaFoldersAndFiles.filesCollection || [],
      totalCount: data?.getMediaFoldersAndFiles.filesTotalCount || 0,
    }),
    [data]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setPerPage(12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    refetch({
      filter: {
        propertyId: property._id,
        mediaFolderId: property.slideshowMediaFolderId,
      },
      pagination: { limit, skip },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, property]);

  const files = useMemo(
    () =>
      mediaFiles.map((item) => {
        const [file] = item.files;

        return {
          _id: item._id,
          name: file?.name,
          publicUrl: file?.publicUrl,
          s3Key: file?.s3Key,
          type: file?.type,
          thumbnailFileUrl: file?.thumbnailFile?.publicUrl,
        };
      }),
    [mediaFiles]
  );

  return (
    <Slideshow
      w="100%"
      mt={4}
      files={files}
      loading={loading}
      onDownloadButtonClick={(fileId) => {
        const mediaToDownload = files.find((item) => item._id === fileId);

        if (mediaToDownload) {
          downloadMedia([mediaToDownload._id]);
        }
      }}
      paginationProps={{
        label: t('pages:propertyPage.paginationLabel'),
        previousButtonLabel: t('common:labels.previous'),
        nextButtonLabel: t('common:labels.next'),
      }}
    />
  );
};

export default PropertySlideshow;
